import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Palette, Save, Loader2, RotateCcw, Eye, EyeOff,
  Globe, Copy, Check, CheckCircle, AlertCircle, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const EMPTY_WL = {
  brand_name: '', logo_url: '', primary_color: '#c9a84c', accent_color: '',
  hide_ostivex_branding: false, powered_by_text: '', support_email: '',
  support_url: '', enabled: true,
};

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs mb-1 font-medium" style={{ color: 'rgba(229,229,229,0.6)' }}>{label}</label>
      {hint && <p className="text-[10px] mb-1.5" style={{ color: 'rgba(229,229,229,0.3)' }}>{hint}</p>}
      {children}
    </div>
  );
}

function Input({ value, onChange, type = 'text', placeholder = '', disabled }) {
  return (
    <input type={type} value={value || ''} onChange={onChange} placeholder={placeholder} disabled={disabled}
      className="w-full px-3 py-2 text-sm outline-none rounded-sm disabled:opacity-40"
      style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text)' }} />
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} data-testid="copy-proxy-config"
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm transition-all"
      style={{ background: copied ? 'rgba(16,185,129,0.1)' : 'var(--lux-surface-3)', border: `1px solid ${copied ? 'rgba(16,185,129,0.3)' : 'var(--lux-border-2)'}`, color: copied ? '#10b981' : 'var(--lux-text-muted)', cursor: 'pointer' }}>
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? 'Copiato!' : 'Copia'}
    </button>
  );
}

/* ── Tenant list row ──────────────────────────────────────────────────────── */
function TenantRow({ tenant, onSelect, selected }) {
  const isElite = ['lifetime', 'elite'].includes(tenant.plan);
  return (
    <button onClick={() => onSelect(tenant.slug)} data-testid={`wl-tenant-${tenant.slug}`}
      className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors"
      style={{
        background: selected ? 'rgba(201,168,76,0.08)' : 'transparent',
        borderLeft: selected ? '2px solid #c9a84c' : '2px solid transparent',
        borderBottom: '1px solid var(--lux-border-2)', cursor: 'pointer',
      }}>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate" style={{ color: selected ? '#c9a84c' : 'var(--lux-text)' }}>{tenant.name}</p>
        <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>{tenant.slug} · {tenant.plan}</p>
        {tenant.custom_domain && (
          <p className="text-[9px] mt-0.5 truncate" style={{ color: '#10b981' }}>
            <Globe size={8} className="inline mr-0.5" />{tenant.custom_domain}
          </p>
        )}
      </div>
      <span className="text-[9px] px-1.5 py-0.5 rounded shrink-0 ml-2"
        style={{ background: isElite ? 'rgba(201,168,76,0.1)' : 'rgba(100,100,100,0.1)', color: isElite ? '#c9a84c' : 'var(--lux-text-muted-3)' }}>
        {isElite ? 'White-label' : 'Standard'}
      </span>
    </button>
  );
}

/* ── Proxy Config Section ─────────────────────────────────────────────────── */
const PROXY_TABS = [
  { key: 'cloudflare_worker', label: 'Cloudflare Worker', badge: 'CONSIGLIATO', color: '#f38020' },
  { key: 'nginx', label: 'Nginx / Apache', badge: null, color: '#4ade80' },
  { key: 'redirect', label: 'Redirect HTML', badge: 'BASE', color: '#a78bfa' },
];

function VerifyStatus({ status }) {
  if (!status) return null;
  const isOk  = status.verified;
  const color = isOk ? '#10b981' : '#ef4444';
  const Icon  = isOk ? CheckCircle : AlertCircle;
  return (
    <div className="mt-4 p-4 rounded-sm flex flex-col gap-2"
      data-testid="proxy-verify-result"
      style={{ background: `${color}0d`, border: `1px solid ${color}30` }}>
      <div className="flex items-center gap-2">
        <Icon size={15} style={{ color }} />
        <span className="text-sm font-medium" style={{ color }}>
          {isOk ? 'Proxy verificato con successo' : 'Verifica non riuscita'}
        </span>
        {status.latency_ms != null && (
          <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full"
            style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
            {status.latency_ms} ms
          </span>
        )}
      </div>
      {isOk ? (
        <p className="text-xs" style={{ color: 'rgba(229,229,229,0.6)' }}>
          Il dominio <strong style={{ color: 'rgba(229,229,229,0.85)' }}>{status.custom_domain}</strong> instrada
          correttamente verso il tenant <code style={{ color: '#c9a84c' }}>{status.slug}</code>.
          La configurazione white-label è attiva.
        </p>
      ) : (
        <p className="text-xs" style={{ color: 'rgba(229,229,229,0.55)' }}>
          {status.error}
        </p>
      )}
      {status.checked_at && (
        <p className="text-[10px]" style={{ color: 'rgba(229,229,229,0.25)' }}>
          Verificato: {new Date(status.checked_at).toLocaleString('it-IT')}
        </p>
      )}
    </div>
  );
}

function ProxySection({ slug, token, tenantDomain }) {
  const [domain, setDomain]         = useState(tenantDomain || '');
  const [saving, setSaving]         = useState(false);
  const [configs, setConfigs]       = useState(null);
  const [activeTab, setActiveTab]   = useState('cloudflare_worker');
  const [loadingCfg, setLoadingCfg] = useState(false);
  const [verifying, setVerifying]   = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const headers = { 'X-Superadmin-Key': token };

  // Load proxy configs when domain is already set
  useEffect(() => {
    setDomain(tenantDomain || '');
    setVerifyResult(null);
    if (tenantDomain) fetchConfigs(tenantDomain);
    else setConfigs(null);
  }, [tenantDomain, slug]);

  const verifyProxy = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await axios.get(`${API}/api/superadmin/proxy-verify/${slug}`, { headers });
      setVerifyResult(data);
    } catch (e) {
      setVerifyResult({
        verified: false,
        error: e.response?.data?.detail || 'Errore durante la verifica.',
        checked_at: new Date().toISOString(),
      });
    } finally { setVerifying(false); }
  };

  const fetchConfigs = async (d) => {
    if (!d) return;
    setLoadingCfg(true);
    try {
      const { data } = await axios.get(`${API}/api/superadmin/proxy-config/${slug}`, { headers });
      setConfigs(data.configs || null);
    } catch { setConfigs(null); }
    finally { setLoadingCfg(false); }
  };

  const saveDomain = async () => {
    const cleaned = domain.replace(/https?:\/\//g, '').toLowerCase().trim().replace(/\/$/, '');
    setSaving(true);
    try {
      await axios.put(`${API}/api/superadmin/proxy-config/${slug}/domain`, { custom_domain: cleaned }, { headers });
      toast.success(cleaned ? `Dominio ${cleaned} salvato.` : 'Dominio rimosso.');
      setDomain(cleaned);
      if (cleaned) await fetchConfigs(cleaned);
      else setConfigs(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio dominio.');
    } finally { setSaving(false); }
  };

  const activeConfig = configs?.[activeTab] || '';

  return (
    <div className="mt-8 pt-8" style={{ borderTop: '1px solid var(--lux-border-1)' }}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-5">
        <Globe size={16} style={{ color: '#c9a84c' }} />
        <h4 className="font-medium text-sm" style={{ color: 'var(--lux-text)' }}>Dominio Custom & Proxy</h4>
        <span className="text-[9px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider"
          style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.2)' }}>
          White-label
        </span>
      </div>

      {/* Spiegazione */}
      <div className="p-4 rounded-sm mb-5 text-xs leading-relaxed" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)' }}>
        <p style={{ color: 'rgba(229,229,229,0.6)' }}>
          Con un dominio custom (es. <span style={{ color: '#c9a84c' }}>portale.villaaurora.com</span>), il tenant accede
          all'area ospiti e al pannello attraverso il proprio brand senza alcun riferimento a Shangrilux.
          Configura un <strong style={{ color: 'rgba(229,229,229,0.8)' }}>proxy</strong> sul dominio del tenant
          che aggiunge <code style={{ color: '#c9a84c' }}>X-Tenant-Slug: {slug}</code> e
          instrada le richieste verso questo deployment.
        </p>
      </div>

      {/* Domain input + azioni */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1">
          <Field label="Dominio custom del tenant" hint="Solo il dominio (es. portale.villaaurora.com) senza https://">
            <Input value={domain} onChange={e => setDomain(e.target.value)}
              placeholder="portale.villaaurora.com" disabled={saving} />
          </Field>
        </div>
        <div className="flex items-end gap-2">
          <button onClick={saveDomain} disabled={saving} data-testid="save-custom-domain"
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-sm"
            style={{ background: '#c9a84c', color: '#000', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, whiteSpace: 'nowrap' }}>
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
          {domain && (
            <button onClick={verifyProxy} disabled={verifying} data-testid="verify-proxy"
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-sm transition-all"
              style={{
                background: 'transparent',
                border: '1px solid rgba(16,185,129,0.4)',
                color: '#10b981',
                cursor: verifying ? 'not-allowed' : 'pointer',
                opacity: verifying ? 0.7 : 1,
                whiteSpace: 'nowrap',
              }}>
              {verifying
                ? <><Loader2 size={12} className="animate-spin" /> Verifica...</>
                : <><CheckCircle size={12} /> Verifica</>
              }
            </button>
          )}
        </div>
      </div>

      {/* Verify result */}
      <VerifyStatus status={verifyResult} />

      {/* DNS istruzioni quick */}
      {domain && (
        <div className="p-4 rounded-sm mt-4 mb-6 text-xs space-y-2" style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-2)' }}>
          <p className="font-semibold text-[11px]" style={{ color: 'var(--lux-text)' }}>Configurazione DNS richiesta</p>
          <div className="space-y-1.5" style={{ color: 'rgba(229,229,229,0.55)' }}>
            <p><span className="font-medium" style={{ color: 'rgba(229,229,229,0.8)' }}>1.</span> Aggiungi un record <code className="px-1 py-0.5 rounded text-[10px]" style={{ background: 'rgba(255,255,255,0.07)' }}>CNAME</code> su <strong style={{ color: '#c9a84c' }}>{domain}</strong> verso il proxy scelto (Cloudflare Worker, VPS, ecc.).</p>
            <p><span className="font-medium" style={{ color: 'rgba(229,229,229,0.8)' }}>2.</span> Il proxy aggiunge automaticamente <code className="px-1 py-0.5 rounded text-[10px]" style={{ background: 'rgba(255,255,255,0.07)' }}>X-Tenant-Slug: {slug}</code> e instrada verso questo deployment.</p>
            <p><span className="font-medium" style={{ color: 'rgba(229,229,229,0.8)' }}>3.</span> Il backend Shangrilux riconosce il tenant e serve la corretta configurazione white-label.</p>
          </div>
          <div className="flex gap-2 pt-3 mt-2 flex-wrap" style={{ borderTop: '1px solid var(--lux-border-1)' }}>
            <a href="/guida-dominio-personalizzato.pdf" target="_blank" rel="noopener noreferrer"
               data-testid="download-domain-guide-it"
               className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-sm font-medium transition-all"
               style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>
              📄 Scarica guida cliente (IT)
            </a>
            <a href="/domain-guide-en.pdf" target="_blank" rel="noopener noreferrer"
               data-testid="download-domain-guide-en"
               className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-sm font-medium transition-all"
               style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
              📄 Download client guide (EN)
            </a>
            <button onClick={() => {
                const emailBody = encodeURIComponent(
                  `Ciao,\n\nabbiamo preparato la configurazione del tuo dominio personalizzato ${domain} sul portale prenotazioni Shangrilux.\n\n` +
                  `In allegato trovi la guida completa passo-passo (a prova d'idiota, 9 pagine) per attivarlo in 15-20 minuti tramite Cloudflare — è gratuito e non richiede competenze tecniche.\n\n` +
                  `Scarica la guida qui:\nhttps://app.shangrilux.com/guida-dominio-personalizzato.pdf\n\n` +
                  `Riepilogo rapido:\n` +
                  `• Crea un account gratuito su cloudflare.com\n` +
                  `• Aggiungi il tuo dominio principale\n` +
                  `• Cambia i nameserver del dominio con quelli forniti da Cloudflare\n` +
                  `• Aggiungi un record CNAME: ${domain.split('.')[0]} → app.shangrilux.com (Proxy: attivo)\n` +
                  `• SSL/TLS → modalità Full\n\n` +
                  `Una volta configurato, dal tuo pannello Shangrilux nella pagina Branding trovi il pulsante "Verifica dominio" che ti mostrerà in tempo reale se tutto è a posto (semaforo verde/giallo/rosso).\n\n` +
                  `Per qualsiasi problema: info@shangrilux.com\n\n` +
                  `Un caro saluto,\nIl team Shangrilux`
                );
                window.location.href = `mailto:?subject=${encodeURIComponent(`Attivazione dominio ${domain} — Shangrilux`)}&body=${emailBody}`;
              }}
              data-testid="send-email-domain-guide"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-sm font-medium transition-all"
              style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
              ✉️ Invia via email
            </button>
          </div>
        </div>
      )}

      {/* Proxy templates */}
      {loadingCfg && (
        <div className="flex items-center gap-2 py-6 justify-center">
          <Loader2 size={14} className="animate-spin" style={{ color: 'var(--lux-text-muted)' }} />
          <span className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>Generazione template...</span>
        </div>
      )}

      {configs && !loadingCfg && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] mb-3 font-medium" style={{ color: 'var(--lux-text-muted)' }}>Scegli il metodo di proxy</p>
          {/* Tab switcher */}
          <div className="flex gap-1 mb-4 flex-wrap">
            {PROXY_TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm transition-all"
                style={{
                  background: activeTab === tab.key ? 'rgba(201,168,76,0.1)' : 'var(--lux-surface-2)',
                  border: activeTab === tab.key ? '1px solid rgba(201,168,76,0.35)' : '1px solid var(--lux-border-2)',
                  color: activeTab === tab.key ? '#c9a84c' : 'var(--lux-text-muted)',
                  cursor: 'pointer',
                }}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: tab.color }} />
                {tab.label}
                {tab.badge && (
                  <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold"
                    style={{ background: activeTab === tab.key ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.07)', color: 'inherit' }}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Description per tab */}
          <div className="mb-3 text-xs" style={{ color: 'rgba(229,229,229,0.45)' }}>
            {activeTab === 'cloudflare_worker' && (
              <p><CheckCircle size={11} className="inline mr-1" style={{ color: '#10b981' }} />
                Soluzione <strong style={{ color: 'rgba(229,229,229,0.7)' }}>gratuita e zero-infrastruttura</strong>. Perfetta se il dominio del tenant è su Cloudflare. Incolla il codice nel Workers Editor e aggiungi il dominio come trigger in 2 minuti.
              </p>
            )}
            {activeTab === 'nginx' && (
              <p><AlertCircle size={11} className="inline mr-1" style={{ color: '#f59e0b' }} />
                Per tenant con <strong style={{ color: 'rgba(229,229,229,0.7)' }}>VPS Linux (Ubuntu/Debian)</strong>. Richiede Nginx installato e certificato SSL via Certbot. Il proxy è completo: SSL terminato, header iniettati, keepalive.
              </p>
            )}
            {activeTab === 'redirect' && (
              <p><AlertCircle size={11} className="inline mr-1" style={{ color: '#a78bfa' }} />
                Soluzione <strong style={{ color: 'rgba(229,229,229,0.7)' }}>base</strong> per hosting condiviso senza accesso server. L'URL nel browser cambia verso app.shangrilux.com — non trasparente al cliente finale.
              </p>
            )}
          </div>

          {/* Code block */}
          <div className="relative rounded-sm overflow-hidden" style={{ border: '1px solid var(--lux-border-2)' }}>
            <div className="flex items-center justify-between px-4 py-2"
              style={{ background: 'var(--lux-surface-2)', borderBottom: '1px solid var(--lux-border-2)' }}>
              <span className="text-[10px] font-mono" style={{ color: 'var(--lux-text-muted)' }}>
                {activeTab === 'cloudflare_worker' ? 'worker.js' : activeTab === 'nginx' ? `${domain}.conf` : 'index.html'}
              </span>
              <CopyButton text={activeConfig} />
            </div>
            <pre className="p-4 text-[11px] font-mono overflow-x-auto whitespace-pre leading-relaxed"
              style={{ background: '#0a0a0a', color: 'rgba(229,229,229,0.75)', maxHeight: '320px', overflowY: 'auto' }}>
              {activeConfig}
            </pre>
          </div>

          {/* Quick Deploy link */}
          {activeTab === 'cloudflare_worker' && (
            <a href="https://dash.cloudflare.com/?to=/:account/workers-and-pages/create/workers/new"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 text-xs"
              style={{ color: '#f38020', textDecoration: 'none' }}>
              Apri Cloudflare Workers Dashboard <ChevronRight size={12} />
            </a>
          )}
        </div>
      )}

      {/* Empty state — nessun dominio */}
      {!domain && !configs && (
        <div className="py-8 text-center rounded-sm" style={{ border: '1px dashed var(--lux-border-2)' }}>
          <Globe size={24} className="mx-auto mb-2" style={{ color: 'var(--lux-text-muted-3)' }} />
          <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>Inserisci un dominio custom e salva per generare i template di configurazione.</p>
        </div>
      )}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────────────────── */
export function WhitelabelTab({ token }) {
  const [tenants, setTenants]       = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [config, setConfig]         = useState(EMPTY_WL);
  const [tenantData, setTenantData] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [preview, setPreview]       = useState(false);
  const headers                     = { 'X-Superadmin-Key': token };

  const loadTenants = useCallback(() => {
    axios.get(`${API}/api/superadmin/tenants`, { headers })
      .then(r => setTenants(r.data.tenants || []))
      .catch(() => toast.error('Errore caricamento tenant'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const selectTenant = async (slug) => {
    setSelectedSlug(slug);
    const td = tenants.find(t => t.slug === slug) || null;
    setTenantData(td);
    try {
      const { data } = await axios.get(`${API}/api/whitelabel/${slug}`, { headers });
      setConfig({ ...EMPTY_WL, ...(data.whitelabel || {}) });
    } catch { setConfig(EMPTY_WL); }
  };

  const set = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  const save = async () => {
    if (!selectedSlug) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/whitelabel/${selectedSlug}`, config, { headers });
      toast.success('Configurazione white-label salvata.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio.'); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!selectedSlug || !window.confirm('Rimuovere la configurazione white-label per questo tenant?')) return;
    try {
      await axios.delete(`${API}/api/whitelabel/${selectedSlug}`, { headers });
      setConfig(EMPTY_WL);
      toast.success('White-label rimosso.');
    } catch { toast.error('Errore rimozione.'); }
  };

  return (
    <div data-testid="whitelabel-tab" className="flex gap-0 min-h-[600px]">
      {/* Lista tenant */}
      <div className="w-56 flex-shrink-0 border-r overflow-y-auto" style={{ borderColor: 'var(--lux-border-1)', background: 'var(--lux-surface-2)' }}>
        <p className="px-4 py-3 text-[10px] uppercase tracking-widest border-b font-medium"
          style={{ color: 'var(--lux-text-muted-3)', borderColor: 'var(--lux-border-2)' }}>Tenant</p>
        {loading
          ? <div className="p-4 text-xs" style={{ color: 'var(--lux-text-muted)' }}>Caricamento...</div>
          : tenants.map(t => (
            <TenantRow key={t.slug} tenant={t} onSelect={selectTenant} selected={selectedSlug === t.slug} />
          ))
        }
      </div>

      {/* Editor */}
      <div className="flex-1 p-8 overflow-y-auto">
        {!selectedSlug ? (
          <div className="h-full flex items-center justify-center text-center">
            <div>
              <Palette size={32} className="mx-auto mb-3" style={{ color: 'var(--lux-text-muted-3)' }} />
              <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>Seleziona un tenant per configurare white-label e dominio custom</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-medium" style={{ color: 'var(--lux-text)' }}>White-label — {selectedSlug}</h3>
                <p className="text-xs mt-0.5" style={{ color: 'var(--lux-text-muted)' }}>Brand personalizzato + dominio custom per tenant</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setPreview(p => !p)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm"
                  style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)', cursor: 'pointer' }}>
                  {preview ? <EyeOff size={12} /> : <Eye size={12} />} {preview ? 'Nascondi preview' : 'Preview'}
                </button>
                <button onClick={reset}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm"
                  style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer' }}>
                  <RotateCcw size={12} /> Reset
                </button>
              </div>
            </div>

            {/* Preview sidebar simulata */}
            {preview && (
              <div className="mb-6 rounded-sm overflow-hidden" style={{ border: '1px solid var(--lux-border-1)', maxWidth: 220 }}>
                <div className="px-4 py-3" style={{ background: config.primary_color || '#c9a84c', opacity: 0.9 }}>
                  <p className="text-xs font-bold text-white uppercase tracking-widest truncate">{config.brand_name || selectedSlug}</p>
                </div>
                {config.logo_url && (
                  <div className="px-4 py-2" style={{ background: '#111' }}>
                    <img src={config.logo_url} alt="logo" className="h-8 object-contain" onError={e => (e.target.style.display = 'none')} />
                  </div>
                )}
                <div className="px-4 py-2 text-[10px]" style={{ background: '#0a0a0a', color: 'rgba(255,255,255,0.3)' }}>
                  {config.hide_ostivex_branding ? (config.powered_by_text || '') : 'Powered by Shangrilux'}
                </div>
              </div>
            )}

            {/* Brand fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
              <Field label="Nome brand personalizzato" hint="Sostituisce 'Shangrilux' nel pannello admin">
                <Input value={config.brand_name} onChange={e => set('brand_name', e.target.value)} placeholder="ShangriLux Hotels" />
              </Field>
              <Field label="URL Logo" hint="Immagine nella sidebar (PNG/SVG trasparente)">
                <Input value={config.logo_url} onChange={e => set('logo_url', e.target.value)} placeholder="https://cdn.example.com/logo.png" />
              </Field>
              <Field label="Colore primario" hint="Sostituisce il dorato Shangrilux (#c9a84c)">
                <div className="flex gap-2">
                  <input type="color" value={config.primary_color || '#c9a84c'} onChange={e => set('primary_color', e.target.value)}
                    className="w-10 h-9 rounded cursor-pointer" style={{ border: '1px solid var(--lux-border-2)', background: 'none', padding: 2 }} />
                  <Input value={config.primary_color} onChange={e => set('primary_color', e.target.value)} placeholder="#c9a84c" />
                </div>
              </Field>
              <Field label="Testo 'Powered by'" hint="Footer sidebar se il branding Shangrilux è nascosto">
                <Input value={config.powered_by_text} onChange={e => set('powered_by_text', e.target.value)} placeholder="Powered by ShangriLux" />
              </Field>
              <Field label="Email supporto custom">
                <Input type="email" value={config.support_email} onChange={e => set('support_email', e.target.value)} placeholder="support@villaaurora.com" />
              </Field>
              <Field label="URL supporto custom">
                <Input value={config.support_url} onChange={e => set('support_url', e.target.value)} placeholder="https://support.villaaurora.com" />
              </Field>
            </div>

            <div className="flex items-center gap-6 mb-6 p-4 rounded-sm"
              style={{ background: 'var(--lux-surface-2)', border: '1px solid var(--lux-border-2)' }}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config.hide_ostivex_branding} onChange={e => set('hide_ostivex_branding', e.target.checked)} />
                <span className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>Nascondi branding Shangrilux</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config.enabled} onChange={e => set('enabled', e.target.checked)} />
                <span className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>White-label attivo</span>
              </label>
            </div>

            <button onClick={save} disabled={saving} data-testid="save-whitelabel"
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-sm"
              style={{ background: '#c9a84c', color: '#000', border: 'none', cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Salvataggio...' : 'Salva configurazione'}
            </button>

            {/* ── Proxy section ─────────────────────────────────────────────── */}
            <ProxySection
              slug={selectedSlug}
              token={token}
              tenantDomain={tenantData?.custom_domain || ''}
            />
          </>
        )}
      </div>
    </div>
  );
}
